import { asyncify, each } from 'async';
import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  createChallengeRecord as restCreateChallengeRecord,
  getChallengeRecords as restGetChallengeRecords,
  getGameOpponents as restGetGameOpponents,
  getGameRecords as restGetGameRecords,
  getRevisionScheduleGame as restGetRevisionScheduleGame,
  getRevisionSchedules as restGetRevisionSchedules,
  syncGameRecords as restSyncGameRecords,
} from '../../api/Rest/record';
import {
  getAlbumSummaries as restGetAlbumSummaries,
  getSpaceSummaries as restGetSpaceSummaries,
  moveInAlbumSummaryPath as restMoveInAlbumSummaryPath,
  updateAlbumSummary as restUpdateAlbumSummary,
  updateAlbumSummaryPath as restUpdateAlbumSummaryPath,
  updateSpaceSummary as restUpdateSpaceSummary,
  viewStartVideo as restViewStartVideo,
} from '../../api/Rest/space';
import { AIRevisionStatus } from '../../constants/MemoryGames/aiRevision';
import { Uuid } from '../../models/Base';
import {
  AlbumSummary,
  AlbumSummaryRepository,
  ChallengeRecord,
  ChallengeRecordRepository,
  GameRecord,
  GameRecordRepository,
  RecordStoreAction,
  RecordStoreState,
  RevisionSchedule,
  RevisionScheduleRepository,
  SpaceSummaryRepository,
} from '../../models/RecordStore';
import { SpaceSummary } from '../../models/SpaceStore';
import {
  createMultipleStoreListFieldHook,
  createStoreListFieldHook,
  createStoreMapFieldHook,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';
import { Profile } from '../../models/UserStore';
import { useUserStore } from '../../stores/UserStore';
import { useSpaceStore } from '../SpaceStore';

import * as State from './state';

type Store = StoreLifecycleAction & RecordStoreState & RecordStoreAction;

const lifecycle = new StoreLifecycle('Recordtore');
lifecycle.finishHydration();

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        gameRecords: new GameRecordRepository(),
        challengeRecords: new ChallengeRecordRepository(),
        albumSummaries: new AlbumSummaryRepository(),
        spaceSummaries: new SpaceSummaryRepository(),
        revisionSchedules: new RevisionScheduleRepository(),
      };
      state.gameRecords.onElementPrune = onGameRecordPrune;
      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  const savePlayingRecord: RecordStoreAction['savePlayingRecord'] = (
    playingRecord,
  ) => {
    set((state) => {
      return { playingRecord: { ...state.playingRecord, ...playingRecord } };
    });
  };

  const syncPlayingRecord: RecordStoreAction['syncPlayingRecord'] =
    async () => {
      const { playingRecord } = get();
      if (playingRecord) {
        const promises: Promise<unknown>[] = [];
        if (playingRecord.gameRecord) {
          // FIXME: remove result containing attachment which is not handled
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { result, ...rest } = playingRecord.gameRecord;
          promises.push(syncGameRecord(rest));
        }
        clearPlayingRecord();
        await Promise.all(promises);
      }
    };

  const clearPlayingRecord: RecordStoreAction['clearPlayingRecord'] = () => {
    set({ playingRecord: undefined });
  };

  const getGameRecords: RecordStoreAction['getGameRecords'] = (fieldName) => {
    return State.getGameRecords(get(), fieldName);
  };

  const getGameRecord: RecordStoreAction['getGameRecord'] = (gameRecordId) =>
    State.getGameRecord(get(), gameRecordId);

  const fetchGameRecords: RecordStoreAction['fetchGameRecords'] = async (
    { fieldName, classroomId, playedBy, gameSetId, gameId, from, to, hash },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetGameRecords(
      { classroomId },
      { playedBy, gameSetId, gameId, cursor: cursor, from, to, hash },
      options,
    );
    if (ok) {
      const newCursor = data.cursor ?? null;
      set((state) => {
        const gameRecords = data.gameRecords.map((c) => new GameRecord(c));
        if (cursor === undefined) {
          State.setGameRecords(state, fieldName, ...gameRecords);
        } else {
          State.pushGameRecords(state, fieldName, ...gameRecords);
        }
        State.setGameRecordCursor(state, fieldName, newCursor);
        return { ...state };
      });
      return {
        shouldFetchMore: !!newCursor && data.gameRecords.length === 0,
      };
    }
    return { shouldFetchMore: false };
  };

  const fetchGameRecordsByGameIds: RecordStoreAction['fetchGameRecordsByGameIds'] =
    async ({ fieldName, classroomId, playedBy, gameIds }, options) => {
      const resultGameRecordt: GameRecord[] = [];
      await each(
        gameIds,
        asyncify(async (gameId: string) => {
          const { ok, data } = await restGetGameRecords(
            { classroomId },
            { playedBy, gameId },
            options,
          );
          if (ok) {
            const gameRecords = data.gameRecords.map((c) => new GameRecord(c));
            resultGameRecordt.push(...gameRecords);
          }
        }),
      );

      set((state) => {
        State.pushGameRecords(state, fieldName, ...resultGameRecordt);
        return { ...state };
      });
    };

  const syncGameRecord: RecordStoreAction['syncGameRecord'] = async (
    gameRecord,
  ) => {
    const result = await restSyncGameRecords({
      gameRecords: [gameRecord],
    });
    const record = result.data?.gameRecords[0];
    if (!record) {
      throw new Error('something went wrong');
    }
    if ('errorMessage' in record) {
      throw new Error(record.errorMessage);
    }

    return record;

    // try {
    // } catch (error) {
    //   if (error instanceof Error) {
    //     console.error('syncGameRecord', error.message);
    //   }
    // }
  };

  const getChallengeRecords: RecordStoreAction['getChallengeRecords'] = (
    fieldName,
  ) => {
    return State.getChallengeRecords(get(), fieldName);
  };

  const getChallengeRecord: RecordStoreAction['getChallengeRecord'] = (
    gameRecordId,
  ) => State.getChallengeRecord(get(), gameRecordId);

  const fetchChallengeRecords: RecordStoreAction['fetchChallengeRecords'] =
    async ({ fieldName, classroomId, gameSetId, hash }, cursor, options) => {
      const { ok, data } = await restGetChallengeRecords(
        { classroomId },
        { gameSetId, cursor: cursor, hash },
        options,
      );
      if (ok) {
        const newCursor = data.cursor ?? null;
        set((state) => {
          const challengeRecords = data.challengeRecords.map(
            (c) => new ChallengeRecord(c),
          );
          if (cursor === undefined) {
            State.setChallengeRecords(state, fieldName, ...challengeRecords);
          } else {
            State.pushChallengeRecords(state, fieldName, ...challengeRecords);
          }
          State.setChallengeRecordCursor(state, fieldName, newCursor);
          return { ...state };
        });
        return {
          shouldFetchMore: !!newCursor && data.challengeRecords.length === 0,
        };
      }
      return { shouldFetchMore: false };
    };

  const createChallengeRecord: RecordStoreAction['createChallengeRecord'] =
    async (challengeRecord) => {
      const { ok, data, error } = await restCreateChallengeRecord(
        { classroomId: challengeRecord.classroomId },
        challengeRecord,
      );
      if (ok) {
        return data.challengeRecord;
      }
      throw error;
    };

  const getAlbumSummary: RecordStoreAction['getAlbumSummary'] = (
    gameSetId,
    userId,
  ) => State.getAlbumSummary(get(), gameSetId, userId);

  const setAlbumSummaryWithListener = (
    state: RecordStoreState,
    gameSetId: Uuid,
    albumSummary: AlbumSummary,
  ) => {
    if (useSpaceStore.getState().getAlbum(gameSetId)) {
      const field = state.albumSummaries.map(`album_${gameSetId}`);
      if (field.isCached()) {
        field.addListener();
      }
      field.set(gameSetId, albumSummary);
      field.setIsStale(false);
    }
  };

  const clearAlbumSummary: RecordStoreAction['clearAlbumSummary'] = (
    gameSetId,
  ) => {
    set((state) => {
      state.albumSummaries.clear(`album_${gameSetId}`);
      state.albumSummaries.map(`album_${gameSetId}`, null)?.removeListener();
      return { ...state };
    });
  };

  const fetchAlbumSummaries: RecordStoreAction['fetchAlbumSummaries'] = async (
    { gameSetIds },
    options,
  ) => {
    const { ok, data } = await restGetAlbumSummaries(
      { gameSetIds: gameSetIds },
      options,
    );
    if (ok) {
      set((state) => {
        gameSetIds.forEach((gameSetId) => {
          const albumSummary = data.gameSetSummaries.find((summary) => {
            return gameSetId === summary.gameSetId;
          });
          if (albumSummary) {
            setAlbumSummaryWithListener(
              state,
              gameSetId,
              new AlbumSummary(albumSummary),
            );
          }
        });
        return { ...state };
      });
    }
  };

  const updateAlbumSummary: RecordStoreAction['updateAlbumSummary'] = async (
    classroomId,
    gameSetId,
  ) => {
    const { ok, data, error } = await restUpdateAlbumSummary({
      classroomId,
      gameSetId,
    });
    if (ok) {
      set((state) => {
        setAlbumSummaryWithListener(
          state,
          gameSetId,
          new AlbumSummary(data.gameSetSummary),
        );
        return { ...state };
      });
    } else {
      console.error(error.message);
    }
  };

  const updateAlbumSummaryPath: RecordStoreAction['updateAlbumSummaryPath'] =
    async (classroomId, gameSetId, { showGameForChallengeOnly }) => {
      const { ok, data, error } = await restUpdateAlbumSummaryPath(
        { classroomId, gameSetId },
        { showGameForChallengeOnly },
      );
      if (ok) {
        set((state) => {
          setAlbumSummaryWithListener(
            state,
            gameSetId,
            new AlbumSummary(data.gameSetSummary),
          );
          return { ...state };
        });
      } else {
        console.error(error.message);
      }
    };

  const moveInAlbumSummaryPath: RecordStoreAction['moveInAlbumSummaryPath'] =
    async (classroomId, gameSetId, moveTo) => {
      const { ok, data, error } = await restMoveInAlbumSummaryPath(
        { classroomId, gameSetId },
        { moveTo },
      );
      if (ok) {
        set((state) => {
          setAlbumSummaryWithListener(
            state,
            gameSetId,
            new AlbumSummary(data.gameSetSummary),
          );
          return { ...state };
        });
      } else {
        console.error(error.message);
      }
    };

  const viewStartVideo: RecordStoreAction['viewStartVideo'] = async (
    classroomId,
    gameSetId,
  ) => {
    const { ok, data, error } = await restViewStartVideo({
      classroomId,
      gameSetId,
    });
    if (ok) {
      set((state) => {
        setAlbumSummaryWithListener(
          state,
          gameSetId,
          new AlbumSummary(data.gameSetSummary),
        );
        return { ...state };
      });
      // TODO: update album with new counter?
    } else {
      console.error(error.message);
    }
  };

  const getSpaceSummary: RecordStoreAction['getSpaceSummary'] = (classroomId) =>
    State.getSpaceSummary(get(), classroomId);

  const fetchSpaceSummaries: RecordStoreAction['fetchSpaceSummaries'] = async (
    { classroomIds },
    options,
  ) => {
    const { ok, data } = await restGetSpaceSummaries(
      { classroomIds: classroomIds },
      options,
    );
    if (ok) {
      set((state) => {
        classroomIds.forEach((classroomId) => {
          const spaceSummary = data.classroomSummaries.find((summary) => {
            return classroomId === summary.classroomId;
          });
          if (spaceSummary) {
            State.addSpaceSummaryToMap(
              state,
              classroomId,
              new SpaceSummary(spaceSummary),
            );
          }
        });
        return { ...state };
      });
    }
  };

  const updateSpaceSummary: RecordStoreAction['updateSpaceSummary'] = async (
    classroomId,
  ) => {
    const { ok, data, error } = await restUpdateSpaceSummary({
      classroomId,
    });
    if (ok) {
      set((state) => {
        State.addSpaceSummaryToMap(
          state,
          classroomId,
          new SpaceSummary(data.classroomSummary),
        );
        return { ...state };
      });
    } else {
      console.error(error.message);
    }
  };

  // opponents
  const fetchGameOpponents: RecordStoreAction['fetchGameOpponents'] = async (
    classroomId,
    gameSetId,
    gameId,
    limit = 1,
  ) => {
    const { ok, data, error } = await restGetGameOpponents(
      { classroomId },
      { gameSetId, gameId, limit, withProfiles: true },
      { headers: { 'cache-control': 'no-cache' } },
    );
    if (ok) {
      useUserStore
        .getState()
        .addProfiles(
          'gamePlay',
          ...data.profiles.map((profile) => new Profile(profile.profile)),
        );
      return data;
    } else {
      console.error(error.message);
      return {
        opponents: [],
        profiles: [],
      };
    }
  };

  const getRevisionSchedule: RecordStoreAction['getRevisionSchedule'] = (
    userId,
    classroomId,
    gameId,
  ) => State.getRevisionSchedule(get(), `${gameId}_${userId}_${classroomId}`);

  const getRevisionSchedules: RecordStoreAction['getRevisionSchedules'] = (
    fieldName,
  ) => State.getRevisionSchedules(get(), fieldName);

  const fetchRevisionSchedules: RecordStoreAction['fetchRevisionSchedules'] =
    async (
      { classroomId, gameSetId, userId, partitionId, webBoosterType, from, to },
      options,
    ) => {
      const { ok, data } = await restGetRevisionSchedules(
        { classroomId: classroomId },
        { gameSetId, partitionId, webBoosterType, from, to }, // limit 10 in backend
        options,
      );
      if (ok) {
        const fieldNamePrefix = `${userId}_${classroomId}_${gameSetId}`;
        set((state) => {
          const readyRevisionSchedules = data.readyRevisionSchedules?.map(
            (r) => new RevisionSchedule(r.revisionSchedule),
          );
          const readyPerformanceGames = data.readyRevisionSchedules?.map(
            (r) => r.data,
          );
          if (readyRevisionSchedules) {
            const fieldName = `${AIRevisionStatus.Ready}_${fieldNamePrefix}`;
            State.setRevisionSchedules(
              state,
              fieldName,
              ...readyRevisionSchedules,
            );
            State.setRevisionScheduleCursor(state, fieldName, data.readyCursor);
            if (readyPerformanceGames) {
              useSpaceStore
                .getState()
                .addPerformanceGamesToField(fieldName, readyPerformanceGames);
            }
          }

          const scheduledRevisionSchedules =
            data.scheduledRevisionSchedules?.map(
              (r) => new RevisionSchedule(r.revisionSchedule),
            );
          const scheduledPerformanceGames =
            data.scheduledRevisionSchedules?.map((r) => r.data);
          if (scheduledRevisionSchedules) {
            const fieldName = `${AIRevisionStatus.Scheduled}_${fieldNamePrefix}`;
            State.setRevisionSchedules(
              state,
              fieldName,
              ...scheduledRevisionSchedules,
            );
            State.setRevisionScheduleCursor(
              state,
              fieldName,
              data.scheduledCursor,
            );
            if (scheduledPerformanceGames) {
              useSpaceStore
                .getState()
                .addPerformanceGamesToField(
                  fieldName,
                  scheduledPerformanceGames,
                );
            }
          }

          const recentRevisionSchedules = data.recentRevisionSchedules?.map(
            (r) => new RevisionSchedule(r.revisionSchedule),
          );
          const recentPerformanceGames = data.recentRevisionSchedules?.map(
            (r) => r.data,
          );
          if (recentRevisionSchedules) {
            const fieldName = `${AIRevisionStatus.Completed}_${fieldNamePrefix}`;
            State.setRevisionSchedules(
              state,
              fieldName,
              ...recentRevisionSchedules,
            );
            if (recentPerformanceGames) {
              useSpaceStore
                .getState()
                .addPerformanceGamesToField(fieldName, recentPerformanceGames);
            }
          }

          return { ...state };
        });
      }
    };

  const fetchRevisionSchedulesByType: RecordStoreAction['fetchRevisionSchedulesByType'] =
    async (
      { revisionType, classroomId, gameSetId, userId },
      cursor,
      options,
    ) => {
      const { ok, data } = await restGetRevisionSchedules(
        { classroomId: classroomId },
        {
          gameSetId,
          readyCursor:
            revisionType === AIRevisionStatus.Ready ? cursor : undefined,
          scheduleCursor:
            revisionType === AIRevisionStatus.Scheduled ? cursor : undefined,
          limit: 20,
        },
        options,
      );
      if (ok) {
        const newCursor = data.cursor ?? null;
        const fieldName = `${revisionType}_${userId}_${classroomId}_${gameSetId}`;
        let revisionSchedules: RevisionSchedule[] | undefined = [];
        if (revisionType === AIRevisionStatus.Ready) {
          revisionSchedules = data.readyRevisionSchedules?.map(
            (r) => new RevisionSchedule(r.revisionSchedule),
          );
        } else if (revisionType === AIRevisionStatus.Scheduled) {
          revisionSchedules = data.scheduledRevisionSchedules?.map(
            (r) => new RevisionSchedule(r.revisionSchedule),
          );
        } else if (revisionType === AIRevisionStatus.Completed) {
          revisionSchedules = data.recentRevisionSchedules?.map(
            (r) => new RevisionSchedule(r.revisionSchedule),
          );
        }
        set((state) => {
          if (revisionSchedules) {
            if (cursor === undefined) {
              State.setRevisionSchedules(
                state,
                fieldName,
                ...revisionSchedules,
              );
            } else {
              State.pushRevisionSchedules(
                state,
                fieldName,
                ...revisionSchedules,
              );
            }
            State.setRevisionScheduleCursor(state, fieldName, newCursor);
          }

          return { ...state };
        });
        return {
          shouldFetchMore: !!newCursor && revisionSchedules?.length === 0,
        };
      }
      return { shouldFetchMore: false };
    };

  const getRevisionScheduleGame: RecordStoreAction['getRevisionScheduleGame'] =
    async (
      { classroomId, gameSetId, partitionId, webBoosterType, from, to },
      options,
    ) => {
      const { ok, data } = await restGetRevisionScheduleGame(
        { classroomId },
        { gameSetId, partitionId, webBoosterType, from, to },
        options,
      );
      if (ok) {
        return data.revisionGame;
      }
      return undefined;
    };

  const onGameRecordPrune: RecordStoreAction['onGameRecordPrune'] = () => {
    set((state) => {
      return { ...state };
    });
  };

  return {
    initialize,
    shutdown,
    playingRecord: undefined,
    gameRecords: new GameRecordRepository(),
    challengeRecords: new ChallengeRecordRepository(),
    albumSummaries: new AlbumSummaryRepository(),
    spaceSummaries: new SpaceSummaryRepository(),
    revisionSchedules: new RevisionScheduleRepository(),
    savePlayingRecord,
    syncPlayingRecord,
    clearPlayingRecord,
    getGameRecords,
    getGameRecord,
    fetchGameRecords,
    fetchGameRecordsByGameIds,
    syncGameRecord,
    getChallengeRecords,
    getChallengeRecord,
    fetchChallengeRecords,
    createChallengeRecord,
    getAlbumSummary,
    clearAlbumSummary,
    fetchAlbumSummaries,
    updateAlbumSummary,
    updateAlbumSummaryPath,
    moveInAlbumSummaryPath,
    viewStartVideo,
    getSpaceSummary,
    fetchSpaceSummaries,
    updateSpaceSummary,
    fetchGameOpponents,
    getRevisionSchedule,
    getRevisionSchedules,
    fetchRevisionSchedules,
    fetchRevisionSchedulesByType,
    getRevisionScheduleGame,
    onGameRecordPrune,
  };
};

export const useRecordStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);
export const useRecordStoreListField = createStoreListFieldHook(useRecordStore);
export const useRecordStoreMultipleListField =
  createMultipleStoreListFieldHook(useRecordStore);
export const useRecordStoreMapField = createStoreMapFieldHook(useRecordStore);
